import React, { useContext, useState, useEffect, useMemo } from "react";
import { IntlContext } from "context";
import { GroupInterface } from "dataProvider/Group";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import exportDataFetcher, { ExportInterface } from "dataProvider/Export";
import Loader from "ui/Loader";
import { matomoPush } from "../helpers";
import ProgressBar from "ui/ProgressBar";

interface Props {
    groups: any[];
    initialValues?: any;
    loading?: (value: boolean) => void;
    searchValue?: string;
}

const GroupForm: React.FC<Props> = ({ groups, initialValues, loading, searchValue }) => {
    const intl = useContext(IntlContext);
    const btnTitle = intl.formatMessage("produit.action.export_format", { format: ".csv" });
    const valueAll = "all";
    const MATOMO_EXPORT_ALL = "tout";
    const MATOMO_EXPORT_PARTIAL = "partiel";
    const [selectedValues, setSelectedValues] = useState<HashMap<any>>(initialValues);
    const [loader, setLoader] = useState<boolean>(false);
    const [exportError, setExportError] = useState<boolean>(false);
    const [exportProgress, setExportProgress] = useState<any>();
    const [progress, setProgress] = useState<number>(0);
    const [matomoValues, setMatomoValues] = useState<any>();
    const [exportAll, setExportAll] = useState<any>();

    // Group normalized names
    const groupsOrder = [
        "description-du-produit",
        "valeurs-nutritionnelles",
        "ingredients",
        "etiquetage-nutritionnel-simplifie",
        "allegations",
        "labels-logos",
        "historique",
        // "sources-des-informations",
    ];

    useEffect(() => {
        setTimeout(() => updateProgress(), 1500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exportProgress]);

    useEffect(() => {
        setExportAll(selectedValues.groups.includes(valueAll));

        const selectedGroups = selectedValues.groups.includes(valueAll)
            ? groups.reduce((memo: string[], group: GroupInterface) => {
                  memo.push(group.normalized_name);
                  return memo;
              }, [])
            : selectedValues.groups;

        const params = Object.assign({}, selectedValues, {
            groups: selectedGroups,
        });

        const matomoValuesTmp = Object.keys(params)
            .reduce((memo: string[], key: string) => {
                const value = Array.isArray(params[key]) ? params[key].join("|") : params[key];

                if (value) {
                    memo.push(`${key}=${value}`);

                    if (key !== "groups") {
                        setExportAll(false);
                    }
                }

                return memo;
            }, [])
            .join("&");
        setMatomoValues(matomoValuesTmp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValues?.groups]);

    const handleImportError = () => {
        setLoading(false);
        setExportError(true);
    };

    const setLoading = (value: boolean) => {
        setLoader(value);
        loading && loading(value);
    };

    const exportProduits = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const selectedGroups = selectedValues.groups.includes(valueAll)
                ? groups.reduce((memo: string[], group: GroupInterface) => {
                      memo.push(group.normalized_name);
                      return memo;
                  }, [])
                : selectedValues.groups;

            const params = Object.assign({}, selectedValues, {
                groups: selectedGroups,
            });

            const hasNotSearchFilters =
                (searchValue ?? "").trim() === "" &&
                params.filter_famille.length === 0 &&
                params.filter_secteur.length === 0;

            if (hasNotSearchFilters) {
                event.preventDefault();
                setLoading(true);

                const promise =
                    selectedValues?.groups[0] === valueAll
                        ? exportDataFetcher.getExportFile()
                        : exportDataFetcher.getSelectedFileFromGroup({ groups: selectedGroups });

                promise
                    .then((data: any) => {
                        const link = document.createElement("a");
                        link.href = URL.createObjectURL(data.content);
                        link.download = data.name;
                        link.click();
                        link.parentNode?.removeChild(link);
                        matomoPush([["trackEvent", MATOMO_EXPORT_ALL, "export", matomoValues, 1]]);
                    })
                    .catch((err) => {
                        matomoPush([["trackEvent", MATOMO_EXPORT_ALL, "export", matomoValues, 0]]);
                        throw err;
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                event.preventDefault();
                setProgress(0);
                if (!Array.isArray(selectedValues?.groups) || !selectedValues.groups.length) {
                    return false;
                }

                setLoading(true);

                exportDataFetcher
                    .export(params)
                    .then((data: any) => {
                        setExportProgress(data);
                    })
                    .catch((err) => {
                        handleImportError();
                        throw err;
                    });
            }
        } catch (e) {
            handleImportError();
        }
    };

    const updateProgress = () => {
        try {
            if (!exportProgress) {
                return;
            }
            const exportMode = exportAll ? MATOMO_EXPORT_ALL : MATOMO_EXPORT_PARTIAL;

            if (exportProgress.status === "ERROR") {
                matomoPush([["trackEvent", exportMode, "export", matomoValues, 0]]);
                handleImportError();
                return;
            }

            // Export terminé
            if (exportProgress.status === "COMPLETED") {
                exportDataFetcher
                    .download(exportProgress.file_id)
                    .then((data: any) => {
                        setExportProgress(data);
                        const link = document.createElement("a");
                        link.href = URL.createObjectURL(data.content);
                        link.download = data.name;
                        link.click();
                        link.parentNode?.removeChild(link);
                        matomoPush([["trackEvent", exportMode, "export", matomoValues, 1]]);
                    })
                    .catch((err) => {
                        matomoPush([["trackEvent", exportMode, "export", matomoValues, 0]]);
                        handleImportError();
                    })
                    .finally(() => {
                        setLoading(false);
                    });
                return;
            }

            if (exportProgress.status !== "PROCESSING" && exportProgress.status !== "PENDING") {
                return;
            }

            // Export en cours
            if (exportProgress.id) {
                exportDataFetcher
                    .one(exportProgress.id)
                    .then((data: ExportInterface) => {
                        setExportProgress(data);
                        setProgress(Math.floor(((data.written_rows || 0) / (data.total_rows || 1)) * 100));
                    })
                    .catch((err) => {
                        handleImportError();
                    })
                    .finally(() => {
                        setLoader(true);
                    });
            }
        } catch (e) {
            handleImportError();
        }
    };

    const sortGroups = (groups: any[]) => {
        return groupsOrder.reduce((memo: any[], key: string) => {
            const found = groups.find((group: any) => group?.normalized_name === key);
            if (found) {
                memo.push(found);
            }
            return memo;
        }, []);
    };

    const getInterfaceGroupName = (name: string) => {
        if (!name) {
            return name;
        }

        if (name.toLowerCase().includes("source")) {
            return intl.formatMessage("produit.fiche.label.sources");
        }

        if (name.toLowerCase().includes("label") || name.includes("logo")) {
            return intl.formatMessage("produit.fiche.label.labels_et_logos");
        }

        return name;
    };

    const groupList = () => {
        return [
            { id: valueAll, normalized_name: valueAll, name: intl.formatMessage("generic.all") },
            ...sortGroups(groups),
            {},
        ].map((group: GroupInterface, index: number) => {
            let groupId;
            let checked;
            let groupName;

            const isEmtpy = Object.keys(group).length === 0;
            if (!isEmtpy) {
                groupId = `group-${group.id}`;
                checked = (selectedValues?.groups || []).includes(group.normalized_name);
                groupName = getInterfaceGroupName(group.name);
            }

            return (
                <li className="popup-item" key={index}>
                    <div className="group-checkbox">
                        {isEmtpy ? null : (
                            <React.Fragment>
                                <input
                                    type="checkbox"
                                    name="groups[]"
                                    id={groupId}
                                    className="popup-checkbox-all"
                                    value={group.normalized_name}
                                    defaultChecked={checked}
                                    checked={checked}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        let groupValues = selectedValues?.groups || [];
                                        const value = e.target.value;
                                        if (e.target.checked) {
                                            if (e.target.value === valueAll) {
                                                groupValues = [valueAll];
                                            } else {
                                                groupValues.push(value);
                                                groupValues = groupValues.filter((v: string) => v !== valueAll);
                                            }
                                        } else {
                                            groupValues = groupValues.filter((v: string) => v !== value);
                                            if (!groupValues.length) {
                                                groupValues = [valueAll];
                                            }
                                        }
                                        setSelectedValues(Object.assign({}, selectedValues, { groups: groupValues }));
                                    }}
                                    disabled={loader}
                                />
                                <label htmlFor={groupId}>{groupName}</label>
                            </React.Fragment>
                        )}
                    </div>
                </li>
            );
        });
    };

    return (
        <React.Fragment>
            <ul className="popup-list-checkbox">{groupList()}</ul>
            {loader ? (
                <React.Fragment>
                    <Loader message={intl.formatMessage("generic.loader.export.label")} />
                    <ProgressBar completed={Math.min(progress, 100)} />
                </React.Fragment>
            ) : (
                <button
                    type="button"
                    className="cta cta--primary margin-auto"
                    title={btnTitle}
                    disabled={loader}
                    onClick={exportProduits}
                >
                    {btnTitle}
                </button>
            )}
            {exportError && <div className="error">{intl.formatMessage("generic.error.export")}</div>}
        </React.Fragment>
    );
};

const ExportForm: React.FC<Props> = ({ groups, initialValues = { groups: [] }, loading, searchValue }) => {
    const initialGroups = useMemo(() => {
        const EXCLUDED_GROUPS = ["sources-des-informations"];
        return (groups || []).filter((group: GroupInterface) => !EXCLUDED_GROUPS.includes(group.normalized_name));
    }, [groups]);

    return (
        <React.Fragment>
            <Form
                autoComplete="off"
                initialValues={initialValues}
                mutators={{ ...arrayMutators }}
                onSubmit={(values) => {}}
                render={({ handleSubmit }) => {
                    return (
                        <form onSubmit={handleSubmit}>
                            <GroupForm
                                groups={initialGroups}
                                initialValues={initialValues}
                                loading={loading}
                                searchValue={searchValue}
                            />
                        </form>
                    );
                }}
            />
        </React.Fragment>
    );
};

export default ExportForm;
